import { PageHero, SectionHeading } from "@/components/site/Sections";
import { CtaButton } from "@/components/site/CtaButton";
import { Reveal } from "@/components/site/Reveal";
import { PACKAGES, ADDONS } from "@/data/content";
import { Link } from "react-router-dom";
import { Check, Info, ArrowRight } from "lucide-react";

export default function Packages() {
  return (
    <div>
      <PageHero
        eyebrow="Packages & Pricing"
        title="From Winning Bid to Recorded Ownership — and Possession."
        subtitle="Flat-fee acquisition support with everything organized in one investor property file. Third-party expenses are always itemized separately."
      />

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            {PACKAGES.map((p, idx) => (
              <Reveal key={p.id} delay={idx * 100}>
                <div className={`relative flex h-full flex-col rounded-2xl border p-8 ${p.highlight ? "border-gold/50 bg-gradient-to-b from-white to-[#FBF9F1] gold-glow" : "border-slate-200 bg-white"}`}>
                  {p.badge && (
                    <span className="absolute -top-3 left-8 rounded-full bg-gold px-4 py-1 font-mono-x text-[10px] tracking-widest text-[#0B132B]">{p.badge}</span>
                  )}
                  <h2 className="text-xl font-semibold text-[#0B132B]">{p.name}</h2>
                  <p className="mt-1 text-sm text-slate-500">{p.subtitle}</p>
                  <div className="mt-5 flex items-end gap-2">
                    <span className="font-display text-5xl font-bold text-gold">{p.price}</span>
                    <span className="mb-2 text-sm text-slate-500">per property</span>
                  </div>
                  {p.intro && <p className="mt-4 text-sm font-medium text-gold/90">{p.intro}</p>}

                  <div className="mt-6 flex-1 space-y-6">
                    {p.groups.map((g) => (
                      <div key={g.label}>
                        <h4 className="font-mono-x text-xs uppercase tracking-widest text-slate-500">{g.label}</h4>
                        <ul className="mt-3 space-y-2">
                          {g.items.map((it) => (
                            <li key={it} className="flex items-start gap-2 text-sm text-slate-600">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {it}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <CtaButton
                    to="/submit-property"
                    size="lg"
                    variant={p.highlight ? "gold" : "outline"}
                    className="mt-8 w-full"
                    data-testid={p.highlight ? "package-select-turnkey-btn" : "package-select-standard-btn"}
                  >
                    {p.cta}
                  </CtaButton>

                  <div className="mt-5 flex items-start gap-2 rounded-lg border border-slate-200 bg-[#FAF8F1] p-3">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                    <p className="text-xs leading-relaxed text-slate-500">{p.disclaimer}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Financing cross-sell */}
      <section className="border-y border-slate-200 bg-[#F5F3EC] py-16">
        <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
          <h3 className="font-display text-2xl font-bold text-[#0B132B]">Need Financing Too?</h3>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-500">
            We can also help coordinate investor financing options including DSCR, hard money, bridge and fix-and-flip financing.
          </p>
          <div className="mt-6">
            <CtaButton to="/request-financing" variant="outline">Explore Financing</CtaButton>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeading center eyebrow="Optional Add-Ons" title="Build Your Own Scope" />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {ADDONS.map((a) => (
              <div key={a.title} className="h-full rounded-xl border border-slate-200 bg-white p-6">
                <h4 className="text-sm font-semibold text-[#0B132B]">{a.title}</h4>
                <p className="mt-2 text-sm text-slate-500">{a.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/how-it-works" className="inline-flex items-center gap-2 text-sm font-semibold text-gold hover:gap-3 transition-all">
              See how the process works <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
